/**
 * New Chat modal — two tabs:
 *   1) "Client" — search clients, expand to their cases, pick case → open case thread.
 *   2) "Group" — title + scope (internal/cross-party) + participant picker.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Search, Users, UserPlus, Loader2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export default function NewChatModal({ open, onClose, onOpenThread }) {
    const [tab, setTab] = useState("client");
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-chat-modal">
            <div className="bg-card border border-border w-full max-w-2xl max-h-[85vh] flex flex-col">
                <header className="flex items-center justify-between border-b border-border px-5 py-3">
                    <div>
                        <div className="overline">Chat</div>
                        <div className="text-base text-foreground mt-0.5">Start a new conversation</div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="new-chat-close">
                        <X className="w-4 h-4"/>
                    </button>
                </header>
                <div className="flex border-b border-border overflow-x-auto scrollbar-hide">
                    <TabBtn active={tab === "client"} onClick={() => setTab("client")} testid="new-chat-tab-client">
                        <UserPlus className="w-3.5 h-3.5"/> With a client
                    </TabBtn>
                    <TabBtn active={tab === "group"} onClick={() => setTab("group")} testid="new-chat-tab-group">
                        <Users className="w-3.5 h-3.5"/> Group chat
                    </TabBtn>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                    {tab === "client" ? <ClientTab onOpenThread={onOpenThread}/> : <GroupTab onOpenThread={onOpenThread}/>}
                </div>
            </div>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-[0.15em] flex items-center justify-center gap-2 ${active ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
        >{children}</button>
    );
}

function ClientTab({ onOpenThread }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        const id = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/chat/clients-search?q=${encodeURIComponent(q)}`);
                setResults(data);
            } catch (e) { toast.error(apiError(e)); }
            finally { setLoading(false); }
        }, 250);
        return () => clearTimeout(id);
    }, [q]);

    const startThread = async (caseId) => {
        try {
            const { data } = await api.post("/chat/threads", { case_id: caseId });
            onOpenThread(data.id);
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div>
            <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <input
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Search by name, email or phone…"
                    className="w-full bg-background border border-border focus:border-primary outline-none pl-9 pr-3 py-2 text-sm"
                    autoFocus
                    data-testid="client-search-input"
                />
            </div>
            <div className="mt-3">
                {loading && <div className="text-xs text-muted-foreground text-center py-4"><Loader2 className="w-3.5 h-3.5 inline animate-spin mr-2"/>Searching…</div>}
                {!loading && results.length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-6" data-testid="client-search-empty">No clients match</div>
                )}
                {!loading && results.map(c => (
                    <div key={c.id} className="border-b border-border" data-testid={`client-search-row-${c.id}`}>
                        <button
                            onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                            className="w-full text-left px-3 py-3 hover:bg-background flex items-center justify-between"
                            data-testid={`client-search-toggle-${c.id}`}
                        >
                            <div>
                                <div className="text-sm text-foreground">{c.name}</div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">{[c.email, c.phone].filter(Boolean).join(" · ") || "—"}</div>
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                                {c.cases.length} case{c.cases.length === 1 ? "" : "s"}
                            </div>
                        </button>
                        {expanded === c.id && (
                            <div className="pl-6 pb-3" data-testid={`client-cases-${c.id}`}>
                                {c.cases.length === 0 && (
                                    <div className="text-[11px] text-muted-foreground py-2">No cases for this client yet.</div>
                                )}
                                {c.cases.map(cs => (
                                    <button
                                        key={cs.id}
                                        onClick={() => startThread(cs.id)}
                                        className="w-full text-left py-2 px-2 hover:bg-background flex items-center gap-2 text-xs"
                                        data-testid={`open-case-thread-${cs.id}`}
                                    >
                                        <Briefcase className="w-3 h-3 text-primary" strokeWidth={1.5}/>
                                        <span className="text-foreground">{cs.title}</span>
                                        {cs.status && <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">· {cs.status}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function GroupTab({ onOpenThread }) {
    const [title, setTitle] = useState("");
    const [scope, setScope] = useState("internal");
    const [candidates, setCandidates] = useState({ internal: [], clients: [] });
    const [picked, setPicked] = useState(new Set());
    const [creating, setCreating] = useState(false);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/chat/group-candidates");
                setCandidates(data);
            } catch (e) { toast.error(apiError(e)); }
        })();
    }, []);

    const togglePick = (id) => {
        setPicked(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    // When scope flips to internal, drop any pre-picked client_portal participants.
    useEffect(() => {
        if (scope !== "internal") return;
        const clientIds = new Set(candidates.clients.map(c => c.id));
        queueMicrotask(() => {
            setPicked(prev => {
                const next = new Set([...prev].filter(id => !clientIds.has(id)));
                return next.size === prev.size ? prev : next;
            });
        });
    }, [scope, candidates.clients]);

    const available = useMemo(() => {
        const rows = [...candidates.internal.map(u => ({ ...u, _kind: "internal" }))];
        if (scope === "cross_party") {
            rows.push(...candidates.clients.map(u => ({ ...u, _kind: "client" })));
        }
        const f = filter.trim().toLowerCase();
        if (!f) return rows;
        return rows.filter(r => (r.name || "").toLowerCase().includes(f) || (r.email || "").toLowerCase().includes(f) || (r.client_name || "").toLowerCase().includes(f));
    }, [candidates, scope, filter]);

    const create = async () => {
        if (!title.trim()) { toast.error("Title is required"); return; }
        if (picked.size === 0) { toast.error("Pick at least one participant"); return; }
        setCreating(true);
        try {
            const { data } = await api.post("/chat/groups", {
                title: title.trim(),
                scope,
                participant_user_ids: [...picked],
            });
            toast.success("Group created");
            onOpenThread(data.id);
        } catch (e) { toast.error(apiError(e)); }
        finally { setCreating(false); }
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="overline">Group name</label>
                <input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder='e.g. "Smith family — estate planning"'
                    className="mt-1.5 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                    data-testid="group-title-input"
                />
            </div>
            <div>
                <label className="overline">Scope</label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                    <ScopeOption active={scope === "internal"} onClick={() => setScope("internal")} testid="group-scope-internal" label="Internal" sub="Firm staff only"/>
                    <ScopeOption active={scope === "cross_party"} onClick={() => setScope("cross_party")} testid="group-scope-cross-party" label="Cross-party" sub="Includes client(s)"/>
                </div>
            </div>
            <div>
                <label className="overline">Participants ({picked.size} selected)</label>
                <input
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    placeholder="Filter by name or email…"
                    className="mt-1.5 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                    data-testid="group-participant-filter"
                />
                <div className="mt-2 max-h-64 overflow-y-auto border border-border">
                    {available.length === 0 && <div className="text-xs text-muted-foreground p-3">No candidates</div>}
                    {available.map(u => (
                        <label
                            key={u.id}
                            className="flex items-center gap-3 px-3 py-2 border-b border-border hover:bg-background cursor-pointer"
                            data-testid={`group-participant-${u.id}`}
                        >
                            <input
                                type="checkbox"
                                checked={picked.has(u.id)}
                                onChange={() => togglePick(u.id)}
                                className="w-3.5 h-3.5 accent-primary"
                            />
                            <div className="flex-1">
                                <div className="text-xs text-foreground">{u.name || u.email}</div>
                                <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mt-0.5">
                                    {u._kind === "client" ? `client portal · ${u.client_name || u.email}` : (u.role || "").replace(/_/g, " ")}
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
            </div>
            <button
                onClick={create}
                disabled={creating || !title.trim() || picked.size === 0}
                className="w-full py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] flex items-center justify-center gap-2"
                data-testid="group-create-btn"
            >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Users className="w-3.5 h-3.5"/>}
                Create group
            </button>
        </div>
    );
}

function ScopeOption({ active, onClick, label, sub, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`text-left px-3 py-2 border ${active ? "border-primary bg-primary/10" : "border-border hover:border-muted"}`}
        >
            <div className="text-sm text-foreground">{label}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
        </button>
    );
}
